import { useState, useEffect } from "react";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  Users, 
  Check, 
  X, 
  Trash2, 
  ShieldOff,
  Mail,
  Calendar,
  Crown,
  Star,
  Sparkles,
  Clock,
  AlertTriangle,
  Infinity,
  KeyRound,
  Copy
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Subscription tier configuration
const TIERS = {
  base: {
    name: "Base",
    icon: Star,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    features: ["materials", "exams"],
    description: "Materiais de estudo e simulados"
  },
  advanced: {
    name: "Avançado",
    icon: Sparkles,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    features: ["clients"],
    description: "Gestão de clientes"
  },
  complete: {
    name: "Completo",
    icon: Crown,
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    features: ["materials", "exams", "clients"],
    description: "Acesso total"
  }
};

// Quick access duration options (days)
const QUICK_DURATIONS = [
  { days: 7, label: "7 dias" },
  { days: 15, label: "15 dias" },
  { days: 30, label: "30 dias" },
  { days: 60, label: "60 dias" },
  { days: 90, label: "90 dias" },
  { days: 180, label: "6 meses" },
  { days: 365, label: "1 ano" },
];

export const AdminUsers = () => {
  const { t, language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteUser, setDeleteUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [accessDays, setAccessDays] = useState("");
  const [accessDate, setAccessDate] = useState("");
  const [accessTab, setAccessTab] = useState("days");
  const [showResetCodeDialog, setShowResetCodeDialog] = useState(false);
  const [resetCode, setResetCode] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  const handleSetTier = async (tier) => {
    if (!selectedUser) return;
    
    try {
      await axios.patch(`${API}/admin/users/${selectedUser.id}/tier`, {
        subscription_tier: tier
      });
      toast.success(`Plano atualizado para ${TIERS[tier].name}!`);
      setShowTierDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error("Erro ao atualizar plano");
    }
  };

  const handleSetAccess = async (days) => {
    if (!selectedUser) return;
    
    try {
      const response = await axios.patch(`${API}/admin/users/${selectedUser.id}/access`, {
        days: days
      });
      toast.success(response.data.message);
      setShowAccessDialog(false);
      setSelectedUser(null);
      setAccessDays("");
      fetchUsers();
    } catch (error) {
      toast.error("Erro ao atualizar acesso");
    }
  };

  const handleSetAccessByDate = async () => {
    if (!selectedUser || !accessDate) return;
    
    try {
      const response = await axios.patch(`${API}/admin/users/${selectedUser.id}/access`, {
        expires_at: accessDate
      });
      toast.success(response.data.message);
      setShowAccessDialog(false);
      setSelectedUser(null);
      setAccessDate("");
      fetchUsers();
    } catch (error) {
      toast.error("Erro ao atualizar acesso");
    }
  };

  const handleRemoveExpiration = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.patch(`${API}/admin/users/${selectedUser.id}/access/remove`);
      toast.success("Acesso ilimitado concedido!");
      setShowAccessDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error("Erro ao remover expiração");
    }
  };

  const handleDeactivate = async (userId) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/deactivate`);
      toast.success("Usuário desativado!");
      fetchUsers();
    } catch (error) {
      toast.error("Erro ao desativar usuário");
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    
    try {
      await axios.delete(`${API}/admin/users/${deleteUser.id}`);
      toast.success("Usuário excluído!");
      setDeleteUser(null);
      fetchUsers();
    } catch (error) {
      toast.error("Erro ao excluir usuário");
    }
  };

  const openTierDialog = (user) => {
    setSelectedUser(user);
    setShowTierDialog(true);
  };

  const openAccessDialog = (user) => {
    setSelectedUser(user);
    setAccessDays("");
    setAccessDate("");
    setAccessTab("days");
    setShowAccessDialog(true);
  };

  const handleGenerateResetCode = async (user) => {
    setSelectedUser(user);
    setGeneratingCode(true);
    setShowResetCodeDialog(true);
    
    try {
      const response = await axios.post(`${API}/admin/users/${user.id}/reset-code`);
      setResetCode(response.data);
    } catch (error) {
      toast.error("Erro ao gerar código");
      setShowResetCodeDialog(false);
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyCodeToClipboard = () => {
    if (resetCode?.code) {
      navigator.clipboard.writeText(resetCode.code);
      toast.success("Código copiado!");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    const locale = language === "pt" ? "pt-BR" : language === "es" ? "es-ES" : "en-US";
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getTierBadge = (tier) => {
    const tierConfig = TIERS[tier] || TIERS.complete;
    const Icon = tierConfig.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-mono uppercase tracking-widest ${tierConfig.color} ${tierConfig.bgColor} border ${tierConfig.borderColor} rounded`}>
        <Icon size={12} />
        {tierConfig.name}
      </span>
    );
  };

  const getDaysRemainingBadge = (user) => {
    if (user.role === "admin") return null;
    
    const days = user.days_remaining;
    
    if (days === null || days === undefined) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Infinity size={12} />
          Ilimitado
        </span>
      );
    }
    
    if (days <= 0) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
          <AlertTriangle size={12} />
          Expirado
        </span>
      );
    }
    
    if (days <= 7) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-orange-600 font-medium">
          <Clock size={12} />
          {days} {days === 1 ? "dia" : "dias"}
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600">
        <Clock size={12} />
        {days} dias
      </span>
    );
  };

  return (
    <Layout>
      <div className="p-6 md:p-12 overflow-x-hidden" data-testid="admin-users-page">
        {/* Header */}
        <div className="admin-header">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
              {t("administration")}
            </p>
            <h1 className="admin-title">{t("users")}</h1>
          </div>
          <div className="text-sm text-muted-foreground">
            {users.filter(u => u.is_active).length} {t("active_of")} {users.length}
          </div>
        </div>

        {/* Users Table - Desktop */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 loading-skeleton" />
            ))}
          </div>
        ) : users.length > 0 ? (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t("users")}</th>
                    <th>{t("email")}</th>
                    <th>{t("plan")}</th>
                    <th>Acesso</th>
                    <th>Status</th>
                    <th>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} data-testid={`user-row-${user.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 border flex items-center justify-center bg-secondary shrink-0">
                            <span className="font-serif">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            {user.role === "admin" && (
                              <span className="text-xs font-mono uppercase tracking-widest text-primary">
                                Admin
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Mail size={14} />
                          {user.email}
                        </span>
                      </td>
                      <td>
                        {user.role === "admin" ? (
                          <span className="text-xs font-mono uppercase tracking-widest text-primary">
                            Admin
                          </span>
                        ) : (
                          getTierBadge(user.subscription_tier || "complete")
                        )}
                      </td>
                      <td>
                        {getDaysRemainingBadge(user)}
                      </td>
                      <td>
                        {user.is_active ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-xs font-mono uppercase tracking-widest">
                            <Check size={14} />
                            {t("active")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-yellow-600 text-xs font-mono uppercase tracking-widest">
                            <X size={14} />
                            {t("pending")}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {user.role !== "admin" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleGenerateResetCode(user)}
                                title="Gerar código de recuperação de senha"
                                data-testid={`reset-code-${user.id}`}
                              >
                                <KeyRound size={14} />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openAccessDialog(user)}
                                title="Definir tempo de acesso"
                                data-testid={`set-access-${user.id}`}
                              >
                                <Clock size={14} />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openTierDialog(user)}
                                title={t("change_plan")}
                                data-testid={`change-tier-${user.id}`}
                              >
                                <Crown size={14} />
                              </Button>
                              {user.is_active && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeactivate(user.id)}
                                  title={t("deactivate")}
                                  data-testid={`deactivate-${user.id}`}
                                >
                                  <ShieldOff size={14} />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDeleteUser(user)}
                                title={t("delete")}
                                data-testid={`delete-${user.id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {users.map((user) => (
                <div 
                  key={user.id} 
                  className="bento-item p-4"
                  data-testid={`user-card-${user.id}`}
                >
                  {/* Header with avatar and name */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 border flex items-center justify-center bg-secondary shrink-0">
                        <span className="font-serif text-lg">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </div>
                    {/* Status Badge */}
                    {user.is_active ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-mono uppercase tracking-widest shrink-0">
                        <Check size={12} />
                        {t("active")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-yellow-600 text-xs font-mono uppercase tracking-widest shrink-0">
                        <X size={12} />
                        {t("pending")}
                      </span>
                    )}
                  </div>

                  {/* Info Grid */}
                  <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground font-mono uppercase mb-1">{t("plan")}</p>
                      {user.role === "admin" ? (
                        <span className="text-xs font-mono uppercase tracking-widest text-primary">Admin</span>
                      ) : (
                        getTierBadge(user.subscription_tier || "complete")
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-mono uppercase mb-1">Acesso</p>
                      {getDaysRemainingBadge(user)}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {user.role !== "admin" && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGenerateResetCode(user)}
                        className="flex-1 min-w-[calc(50%-4px)]"
                        data-testid={`reset-code-mobile-${user.id}`}
                      >
                        <KeyRound size={14} className="mr-1" />
                        <span className="text-xs">Senha</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openAccessDialog(user)}
                        className="flex-1 min-w-[calc(50%-4px)]"
                        data-testid={`set-access-mobile-${user.id}`}
                      >
                        <Clock size={14} className="mr-1" />
                        <span className="text-xs">Acesso</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openTierDialog(user)}
                        className="flex-1 min-w-[calc(50%-4px)]"
                        data-testid={`change-tier-mobile-${user.id}`}
                      >
                        <Crown size={14} className="mr-1" />
                        <span className="text-xs">Plano</span>
                      </Button>
                      {user.is_active ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeactivate(user.id)}
                          className="flex-1 min-w-[calc(50%-4px)]"
                          data-testid={`deactivate-mobile-${user.id}`}
                        >
                          <ShieldOff size={14} className="mr-1" />
                          <span className="text-xs">Desativar</span>
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteUser(user)}
                          className="flex-1 min-w-[calc(50%-4px)] text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-mobile-${user.id}`}
                        >
                          <Trash2 size={14} className="mr-1" />
                          <span className="text-xs">Excluir</span>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="empty-state">
            <Users className="empty-state-icon" />
            <p className="empty-state-title">{t("no_users")}</p>
          </div>
        )}

        {/* Access Duration Dialog */}
        <Dialog open={showAccessDialog} onOpenChange={setShowAccessDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                Definir Tempo de Acesso
              </DialogTitle>
              <DialogDescription>
                Escolha a duração do acesso para <strong>{selectedUser?.name}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <Tabs value={accessTab} onValueChange={setAccessTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="days">Por Dias</TabsTrigger>
                <TabsTrigger value="date">Por Data</TabsTrigger>
              </TabsList>
              
              <TabsContent value="days" className="space-y-4 pt-4">
                {/* Quick options */}
                <div className="grid grid-cols-3 gap-2">
                  {QUICK_DURATIONS.map((opt) => (
                    <Button
                      key={opt.days}
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetAccess(opt.days)}
                      className="h-10"
                      data-testid={`quick-access-${opt.days}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
                
                {/* Custom days input */}
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Dias personalizados"
                    value={accessDays}
                    onChange={(e) => setAccessDays(e.target.value)}
                    min="1"
                    className="flex-1"
                    data-testid="custom-days-input"
                  />
                  <Button
                    onClick={() => accessDays && handleSetAccess(parseInt(accessDays))}
                    disabled={!accessDays || parseInt(accessDays) < 1}
                    data-testid="apply-custom-days"
                  >
                    Aplicar
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="date" className="space-y-4 pt-4">
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">
                    Data de expiração
                  </label>
                  <Input
                    type="date"
                    value={accessDate}
                    onChange={(e) => setAccessDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="access-date-input"
                  />
                </div>
                <Button
                  onClick={handleSetAccessByDate}
                  disabled={!accessDate}
                  className="w-full"
                  data-testid="apply-date"
                >
                  Definir Data
                </Button>
              </TabsContent>
            </Tabs>
            
            {/* Unlimited access option */}
            <div className="border-t pt-4 mt-4">
              <Button
                variant="ghost"
                onClick={handleRemoveExpiration}
                className="w-full text-muted-foreground hover:text-foreground"
                data-testid="unlimited-access"
              >
                <Infinity size={16} className="mr-2" />
                Conceder Acesso Ilimitado
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Tier Selection Dialog */}
        <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {t("select_plan")}
              </DialogTitle>
              <DialogDescription>
                {t("select_plan_for")} <strong>{selectedUser?.name}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-3 py-4">
              {Object.entries(TIERS).map(([key, tier]) => {
                const Icon = tier.icon;
                const isSelected = selectedUser?.subscription_tier === key;
                
                return (
                  <button
                    key={key}
                    onClick={() => handleSetTier(key)}
                    className={`w-full p-4 border rounded-lg text-left transition-all hover:shadow-md ${
                      isSelected 
                        ? `${tier.borderColor} ${tier.bgColor} border-2` 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid={`tier-option-${key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tier.bgColor}`}>
                        <Icon className={tier.color} size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-serif text-lg">{tier.name}</h3>
                          {isSelected && (
                            <Check className="text-green-600" size={18} />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {tier.description}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {tier.features.includes("materials") && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                              Materiais
                            </span>
                          )}
                          {tier.features.includes("exams") && (
                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                              Simulados
                            </span>
                          )}
                          {tier.features.includes("clients") && (
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                              Clientes
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Code Dialog */}
        <Dialog open={showResetCodeDialog} onOpenChange={(open) => {
          setShowResetCodeDialog(open);
          if (!open) {
            setResetCode(null);
            setSelectedUser(null);
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                <KeyRound size={20} />
                Código de Recuperação
              </DialogTitle>
              <DialogDescription>
                {generatingCode 
                  ? "Gerando código..." 
                  : `Código para ${selectedUser?.name} (${selectedUser?.email})`
                }
              </DialogDescription>
            </DialogHeader>
            
            {generatingCode ? (
              <div className="py-8 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : resetCode && (
              <div className="space-y-4 py-4">
                <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6 text-center">
                  <p className="text-xs text-muted-foreground mb-2 font-mono uppercase tracking-widest">
                    Código de 6 dígitos
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-4xl font-mono font-bold tracking-[0.5em] text-primary">
                      {resetCode.code}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyCodeToClipboard}
                      title="Copiar código"
                    >
                      <Copy size={18} />
                    </Button>
                  </div>
                </div>
                
                <div className="text-sm text-muted-foreground space-y-2">
                  <p className="flex items-center gap-2">
                    <Clock size={14} />
                    Válido por <strong>24 horas</strong>
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail size={14} />
                    Envie este código para o usuário via WhatsApp ou outro meio
                  </p>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  <strong>Instruções para o usuário:</strong>
                  <ol className="list-decimal list-inside mt-1 space-y-1">
                    <li>Acessar a tela de login</li>
                    <li>Clicar em "Esqueci minha senha"</li>
                    <li>Digitar o email e o código acima</li>
                    <li>Criar uma nova senha</li>
                  </ol>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-xl">
                {t("delete_user")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("delete_user_warning")} {deleteUser?.name} {t("will_be_deleted")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete"
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AdminUsers;
